import { useCallback } from 'react';
import styled from 'styled-components';
import { SORT_DIRECTIONS } from 'constants/sortDirectionsEnum';
import { DoubleArrow } from 'ui/icons/navigation/DoubleArrow';
import { DownArrow } from 'ui/icons/navigation/DownArrow';
import { UpArrow } from 'ui/icons/navigation/UpArrow';
import { button, colors, spacing, textColors } from 'ui/theme';
import { useSortingContext } from 'utils/contexts/sorting';
import { TABLE_COLUMNS } from '../constants';
import { Cell } from './Cell';

type Column = {
  id: TABLE_COLUMNS;
  label: string;
  initialSortDirection?: SORT_DIRECTIONS;
  isSortable: boolean;
};

interface HeaderRowCellProps {
  column: Column;
}

export const CellWrapper = styled(Cell).attrs((props: { isSortable: boolean }) => ({
  as: props.isSortable ? 'button' : 'div',
}))<{
  isActiveSortColumn: boolean;
  columnId: string;
  isSortable: boolean;
}>`
  ${button}
  display: flex;
  align-items: center;
  background: none;
  border: none;
  color: ${(props) => (props.isActiveSortColumn ? colors.brandPurple : colors.grey50)};
  outline: none;
  text-align: left;
  white-space: nowrap;

  .list-header-label {
    display: inline-block;
    margin-right: ${spacing.quarter};
    color: ${textColors.textPrimary};
  }
`;

export const HeaderRowCell = ({ column }: HeaderRowCellProps) => {
  const { sortBy, setSortBy, sortDirection, setSortDirection } = useSortingContext();
  const isAscendingSort = sortDirection === SORT_DIRECTIONS.ASCENDING;
  const isActiveSortColumn = sortBy === column.id;

  const handleClick = useCallback(() => {
    if (isActiveSortColumn) {
      setSortDirection(isAscendingSort ? SORT_DIRECTIONS.DESCENDING : SORT_DIRECTIONS.ASCENDING);
    } else {
      setSortBy(column.id);
      setSortDirection(column.initialSortDirection || SORT_DIRECTIONS.DESCENDING);
    }
  }, [
    setSortBy,
    setSortDirection,
    column.id,
    isAscendingSort,
    isActiveSortColumn,
    column.initialSortDirection,
  ]);

  return (
    <CellWrapper
      data-test={`column-header-${column.id}`}
      columnId={column.id}
      isActiveSortColumn={isActiveSortColumn}
      key={column.id}
      $isEven={true}
      onClick={handleClick}
      isSortable={column.isSortable}
    >
      <span className="list-header-label" data-test="header-row-label">
        {column.label}
      </span>
      {!isActiveSortColumn && column.isSortable && <DoubleArrow isColorInherited />}
      {isActiveSortColumn && column.isSortable && isAscendingSort && <UpArrow isColorInherited />}
      {isActiveSortColumn && column.isSortable && !isAscendingSort && (
        <DownArrow isColorInherited />
      )}
    </CellWrapper>
  );
};
